import { SHIP_STATS } from '../../shared/constants';
import { hexVecLength } from '../../shared/hex';
import type { PlayerId, Ship } from '../../shared/types/domain';
import { formatCapacity } from './formatters';

export interface ShipDetailRowView {
  label: string;
  value: string;
  tone: 'warning' | 'danger' | 'success' | null;
}

export interface ShipListEntryView {
  shipId: string;
  displayName: string;
  isSelected: boolean;
  isDestroyed: boolean;
  statusText: string;
  hasBurn: boolean;
  fuelText: string;
  passengerText: string;
  detailRows: ShipDetailRowView[];
  // Whose fleet this ship belongs to, for the spectator/replay side-by-side
  // view. Null when the viewer is the owner — the full fleet is already
  // implicitly the viewer's, so no extra label is needed.
  ownerClass: 'fleet-one' | 'fleet-two' | null;
  ownerLabel: string | null;
  // True when this ship belongs to the player whose turn is currently
  // being replayed, so the list can emphasize which fleet is acting right
  // now. Ignored in first-person gameplay.
  isActivePlayerShip: boolean;
}

const getDisplayNames = (ships: Ship[]) => {
  const typeCounts = ships.reduce<Record<string, number>>((acc, ship) => {
    acc[ship.type] = (acc[ship.type] ?? 0) + 1;

    return acc;
  }, {});

  const typeIndices: Record<string, number> = {};

  return ships.map((ship) => {
    const name = SHIP_STATS[ship.type]?.name ?? ship.type;
    const needsNumber = (typeCounts[ship.type] ?? 0) > 1;

    typeIndices[ship.type] = (typeIndices[ship.type] ?? 0) + 1;

    return needsNumber ? `${name} ${typeIndices[ship.type]}` : name;
  });
};

const getStatusText = (ship: Ship): string =>
  [
    ship.lifecycle === 'destroyed'
      ? 'X'
      : ship.control === 'captured'
        ? 'CAP'
        : ship.damage.disabledTurns > 0
          ? `D${ship.damage.disabledTurns}`
          : '',
    ship.heroismAvailable ? 'H' : '',
  ]
    .filter(Boolean)
    .join(' ');

const getVelocityLabel = (ship: Ship): string => {
  const speed = hexVecLength(ship.velocity);

  return speed === 0 ? 'Stationary' : `${speed}`;
};

const getShipDetailRows = (
  ship: Ship,
  isSelected: boolean,
  compact: boolean,
): ShipDetailRowView[] => {
  const stats = SHIP_STATS[ship.type];

  if (!isSelected || ship.lifecycle === 'destroyed' || !stats) {
    return [];
  }

  const combatRow: ShipDetailRowView = {
    label: 'Combat',
    value: `${stats.combat}${stats.defensiveOnly ? ' (def)' : ''}${ship.heroismAvailable ? ' \u2605' : ''}`,
    tone: null,
  };

  const velocityRow: ShipDetailRowView = {
    label: 'Velocity',
    value: getVelocityLabel(ship),
    tone: null,
  };

  const exceptionalRows = (
    [
      ship.damage.disabledTurns > 0
        ? {
            label: 'Disabled',
            value: `${ship.damage.disabledTurns} turns`,
            tone: 'warning' as const,
          }
        : null,
      ship.control === 'captured'
        ? {
            label: 'Status',
            value: 'Captured',
            tone: 'danger' as const,
          }
        : null,
      ship.control !== 'captured' && ship.lifecycle === 'landed'
        ? {
            label: 'Status',
            value: 'Landed',
            tone: 'success' as const,
          }
        : null,
    ] as (ShipDetailRowView | null)[]
  ).filter((row): row is ShipDetailRowView => row !== null);

  if (compact) {
    const speed = hexVecLength(ship.velocity);
    const rows: ShipDetailRowView[] = [combatRow];

    if (speed > 0) {
      rows.push(velocityRow);
    }

    rows.push(...exceptionalRows);

    return rows;
  }

  return [
    combatRow,
    stats.cargo > 0
      ? {
          label: 'Cargo',
          value: `${formatCapacity(stats.cargo - ship.cargoUsed)}/${formatCapacity(stats.cargo)}`,
          tone: null,
        }
      : null,
    (ship.passengersAboard ?? 0) > 0
      ? {
          label: 'Passengers',
          value: `${ship.passengersAboard}`,
          tone: 'success' as const,
        }
      : null,
    velocityRow,
    ...exceptionalRows,
  ].filter((row): row is ShipDetailRowView => row !== null);
};

export interface ShipListSideContext {
  viewerId: PlayerId | -1;
  activePlayer: PlayerId;
}

export const buildShipListView = (
  ships: Ship[],
  selectedId: string | null,
  burns: Map<string, number | null>,
  compact = false,
  sideContext: ShipListSideContext | null = null,
): ShipListEntryView[] => {
  const displayNames = getDisplayNames(ships);
  // Side-context decorations (owner badge + active-player highlight) are
  // only meaningful in spectator / replay views. In first-person play
  // the ship list is already filtered to the viewer's own fleet, so
  // labeling every row "P1" would be noise.
  const showOwner =
    sideContext !== null && (sideContext.viewerId as number) < 0;

  return ships.map((ship, index) => {
    const ownerClass: 'fleet-one' | 'fleet-two' | null = showOwner
      ? ship.owner === 0
        ? 'fleet-one'
        : 'fleet-two'
      : null;
    const ownerLabel = showOwner ? `P${ship.owner + 1}` : null;
    const isActivePlayerShip =
      sideContext !== null && ship.owner === sideContext.activePlayer;
    const isOwnFugitive =
      ship.identity?.hasFugitives === true &&
      (sideContext === null || sideContext.viewerId === ship.owner);

    return {
      shipId: ship.id,
      displayName: `${displayNames[index]}${isOwnFugitive ? ' ★' : ''}`,
      isSelected: ship.id === selectedId,
      isDestroyed: ship.lifecycle === 'destroyed',
      statusText: getStatusText(ship),
      hasBurn: burns.has(ship.id) && burns.get(ship.id) !== null,
      fuelText:
        ship.lifecycle === 'destroyed'
          ? ''
          : `${formatCapacity(ship.fuel)}/${
              SHIP_STATS[ship.type]
                ? formatCapacity(SHIP_STATS[ship.type].fuel)
                : '?'
            }`,
      passengerText:
        (ship.passengersAboard ?? 0) > 0
          ? `${ship.passengersAboard} passengers`
          : '',
      detailRows: getShipDetailRows(ship, ship.id === selectedId, compact),
      ownerClass,
      ownerLabel,
      isActivePlayerShip,
    };
  });
};
